import { FullValidationErrorFragment } from '@common/graphql/fragments';
import { gql } from '@common/hooks';
import { HistoryInfoFragment } from '@common/graphql';

const IssuerList = gql`
  query IssuerList {
    issuerList {
      id
      name
    }
  }
`;

const DocumentTemplates = gql`
  query DocumentTemplates($categories: [String!]) {
    documentTemplates(categories: $categories) {
      id
      name
      types
      expirationType
      daysToExpiry
    }
  }
`;

const CertificationTypes = gql`
  query CertificationTypes {
    certificationList {
      id
      name
      credential
      issuingBodies
    }
  }
`;

const AssessmentTypes = gql`
  query AssessmentTypes {
    assessmentList {
      id
      name
      category
    }
  }
`;

const HealthDocTypes = gql`
  query HealthDocTypes {
    availableHealthDocs {
      id
      name
      categories(filter: PROVIDER)
      expirationType
    }
  }
`;

const WorksListItemTemplatesInfo = gql`
  # FIXME duplicated from works-list-template.gql
  # How can I reuse?
  fragment WorksListItemTemplatesInfo on WorksListTemplate {
    id
    worksListItemTemplates {
      id
      active
      name
      type
      description
      expirationType
      daysToExpiry

      ... on BackgroundIdentificationItemTemplate {
        documentTemplateSubtype
        documentTemplate {
          id
        }
      }
      ... on ImmigrationItemTemplate {
        documentTemplateSubtype
        documentTemplate {
          id
        }
      }
      ... on CertificationItemTemplate {
        certification {
          id
        }
      }
      ... on LicenseItemTemplate {
        licenseType
      }
      ... on AssessmentItemTemplate {
        assessment {
          id
        }
      }
      ... on HealthDocumentItemTemplate {
        healthDocSubtype
        seasonStartsAt
        seasonEndsAt
        healthDoc {
          id
        }
      }
    }
  }
`;

const CreateBackgroundIdentificationItemTemplate = gql`
  mutation CreateBackgroundIdentificationItemTemplate(
    $worksListTemplateId: ID!
    $input: CreateBackgroundIdentificationItemTemplateInput!
  ) {
    createBackgroundIdentificationItemTemplate(
      worksListTemplateId: $worksListTemplateId
      input: $input
    ) {
      worksListItemTemplate {
        id
        ... on BackgroundIdentificationItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const UpdateBackgroundIdentificationItemTemplate = gql`
  mutation UpdateBackgroundIdentificationItemTemplate(
    $itemId: ID!
    $input: UpdateBackgroundIdentificationItemTemplateInput!
  ) {
    updateBackgroundIdentificationItemTemplate(itemId: $itemId, input: $input) {
      worksListItemTemplate {
        id
        ... on BackgroundIdentificationItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const CreateImmigrationItemTemplate = gql`
  mutation CreateImmigrationItemTemplate(
    $worksListTemplateId: ID!
    $input: CreateImmigrationItemTemplateInput!
  ) {
    createImmigrationItemTemplate(
      worksListTemplateId: $worksListTemplateId
      input: $input
    ) {
      worksListItemTemplate {
        id
        ... on ImmigrationItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const UpdateImmigrationItemTemplate = gql`
  mutation UpdateImmigrationItemTemplate(
    $itemId: ID!
    $input: UpdateImmigrationItemTemplateInput!
  ) {
    updateImmigrationItemTemplate(itemId: $itemId, input: $input) {
      worksListItemTemplate {
        id
        ... on ImmigrationItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const CreateLicenseItemTemplate = gql`
  mutation CreateLicenseItemTemplate(
    $worksListTemplateId: ID!
    $input: CreateLicenseItemTemplateInput!
  ) {
    createLicenseItemTemplate(
      worksListTemplateId: $worksListTemplateId
      input: $input
    ) {
      worksListItemTemplate {
        id
        ... on LicenseItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const UpdateLicenseItemTemplate = gql`
  mutation UpdateLicenseItemTemplate(
    $itemId: ID!
    $input: UpdateLicenseItemTemplateInput!
  ) {
    updateLicenseItemTemplate(itemId: $itemId, input: $input) {
      worksListItemTemplate {
        id
        ... on LicenseItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const CreateCertificationItemTemplate = gql`
  mutation CreateCertificationItemTemplate(
    $worksListTemplateId: ID!
    $input: CreateCertificationItemTemplateInput!
  ) {
    createCertificationItemTemplate(
      worksListTemplateId: $worksListTemplateId
      input: $input
    ) {
      worksListItemTemplate {
        id
        ... on CertificationItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const UpdateCertificationItemTemplate = gql`
  mutation UpdateCertificationItemTemplate(
    $itemId: ID!
    $input: UpdateCertificationItemTemplateInput!
  ) {
    updateCertificationItemTemplate(itemId: $itemId, input: $input) {
      worksListItemTemplate {
        id
        ... on CertificationItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const CreateAssessmentItemTemplate = gql`
  mutation CreateAssessmentItemTemplate(
    $worksListTemplateId: ID!
    $input: CreateAssessmentItemTemplateInput!
  ) {
    createAssessmentItemTemplate(
      worksListTemplateId: $worksListTemplateId
      input: $input
    ) {
      worksListItemTemplate {
        id
        ... on AssessmentItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const UpdateAssessmentItemTemplate = gql`
  mutation UpdateAssessmentItemTemplate(
    $itemId: ID!
    $input: UpdateAssessmentItemTemplateInput!
  ) {
    updateAssessmentItemTemplate(itemId: $itemId, input: $input) {
      worksListItemTemplate {
        id
        ... on AssessmentItemTemplate {
          id
          assessmentIssuers {
            id
            name
          }
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const CreateHealthDocumentItemTemplate = gql`
  mutation CreateHealthDocumentItemTemplate(
    $worksListTemplateId: ID!
    $input: HealthDocumentItemTemplateInput!
  ) {
    createHealthDocumentItemTemplate(
      worksListTemplateId: $worksListTemplateId
      input: $input
    ) {
      worksListItemTemplate {
        id
        ... on HealthDocumentItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const UpdateHealthDocumentItemTemplate = gql`
  mutation UpdateHealthDocumentItemTemplate(
    $itemId: ID!
    $input: HealthDocumentItemTemplateInput!
  ) {
    updateHealthDocumentItemTemplate(itemId: $itemId, input: $input) {
      worksListItemTemplate {
        id
        ... on HealthDocumentItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const CreateOtherDocumentItemTemplate = gql`
  mutation CreateOtherDocumentItemTemplate(
    $worksListTemplateId: ID!
    $input: CreateOtherDocumentItemTemplateInput!
  ) {
    createOtherDocumentItemTemplate(
      worksListTemplateId: $worksListTemplateId
      input: $input
    ) {
      worksListItemTemplate {
        id
        ... on OtherDocumentItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

const UpdateOtherDocumentItemTemplate = gql`
  mutation UpdateOtherDocumentItemTemplate(
    $itemId: ID!
    $input: UpdateOtherDocumentItemTemplateInput!
  ) {
    updateOtherDocumentItemTemplate(itemId: $itemId, input: $input) {
      worksListItemTemplate {
        id
        ... on OtherDocumentItemTemplate {
          id
        }
      }
      worksListTemplate {
        id
        bulkUpdateState
        ...WorksListItemTemplatesInfo
        history {
          ...HistoryInfoFragment
        }
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${WorksListItemTemplatesInfo}
  ${HistoryInfoFragment}
  ${FullValidationErrorFragment}
`;

export {
  IssuerList,
  DocumentTemplates,
  CertificationTypes,
  AssessmentTypes,
  HealthDocTypes,
  CreateBackgroundIdentificationItemTemplate,
  UpdateBackgroundIdentificationItemTemplate,
  CreateImmigrationItemTemplate,
  UpdateImmigrationItemTemplate,
  CreateLicenseItemTemplate,
  UpdateLicenseItemTemplate,
  CreateCertificationItemTemplate,
  UpdateCertificationItemTemplate,
  CreateAssessmentItemTemplate,
  UpdateAssessmentItemTemplate,
  CreateHealthDocumentItemTemplate,
  UpdateHealthDocumentItemTemplate,
  CreateOtherDocumentItemTemplate,
  UpdateOtherDocumentItemTemplate,
};
